/**
 * W5 — Per-user stats view.
 *
 * Renders the `GET /api/v1/agency/stats/per-user` breakdown as a table of
 * employee rows: name, role chip, proposed / approved / denied counts, an
 * approval-rate bar, and last-active timestamp.
 *
 * Reuses the same window chips (7d/30d/90d) and legacy theme variables as the
 * sibling `StatsView`. No chart libraries, no MUI — plain divs + custom SCSS
 * scoped under `.perUserStats` in `src/styles/pages/stats/stats.scss`.
 */
import React, { useMemo } from 'react';
import {
  PerUserStatsResponse,
  PerUserStatsRow,
  StatsWindow,
} from '../../lib/api/audit/audit-api';

export interface PerUserStatsViewProps {
  data: PerUserStatsResponse | null;
  window: StatsWindow;
  onWindowChange: (w: StatsWindow) => void;
  loading: boolean;
  error?: string | null;
  onRetry?: () => void;
}

// ---------- helpers ----------

function formatPct(value: number): string {
  if (!Number.isFinite(value)) return '0%';
  return `${Math.round(value * 100)}%`;
}

/** Humanise an ISO timestamp into a short relative string. */
function formatLastActive(iso: string | null): string {
  if (!iso) return 'Never';
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return '—';
  const diffMs = Date.now() - then.getTime();
  if (diffMs < 0) return then.toLocaleDateString();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return then.toLocaleDateString();
}

/** Map a role string to a stable modifier class for the chip colour. */
function roleModifier(role: string): string {
  const r = (role || '').toLowerCase();
  if (r === 'owner') return 'perUserStats__roleChip--owner';
  if (r === 'team_lead') return 'perUserStats__roleChip--lead';
  if (r === 'manager') return 'perUserStats__roleChip--manager';
  if (r === 'analyst') return 'perUserStats__roleChip--analyst';
  if (r === 'read_only') return 'perUserStats__roleChip--readonly';
  return '';
}

/** Humanise a role token for display (e.g. `team_lead` → `Team lead`). */
function roleLabel(role: string): string {
  if (!role) return '—';
  const spaced = role.replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

// ---------- sub-components ----------

const WindowChips: React.FC<{
  window: StatsWindow;
  onWindowChange: (w: StatsWindow) => void;
}> = ({ window, onWindowChange }) => {
  const windowOptions: Array<{ value: StatsWindow; label: string }> = [
    { value: '7d', label: 'Last 7 days' },
    { value: '30d', label: 'Last 30 days' },
    { value: '90d', label: 'Last 90 days' },
  ];
  return (
    <div className='statsView__chips'>
      {windowOptions.map((opt) => (
        <button
          key={opt.value}
          type='button'
          className={`statsView__chip ${
            window === opt.value ? 'statsView__chip--active' : ''
          }`}
          onClick={() => onWindowChange(opt.value)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
};

const ApprovalBar: React.FC<{ rate: number }> = ({ rate }) => {
  const width = Math.max(0, Math.min(100, (rate || 0) * 100));
  return (
    <div className='perUserStats__approval'>
      <div className='perUserStats__approvalTrack'>
        <div
          className='perUserStats__approvalFill'
          style={{ width: `${width}%` }}
        />
      </div>
      <span className='perUserStats__approvalPct'>{formatPct(rate)}</span>
    </div>
  );
};

const PerUserRow: React.FC<{ row: PerUserStatsRow }> = ({ row }) => (
  <div className='perUserStats__row'>
    <div className='perUserStats__cell perUserStats__cell--employee'>
      <div className='perUserStats__name'>{row.name || row.email || '—'}</div>
      {row.email && row.name && (
        <div className='perUserStats__email'>{row.email}</div>
      )}
    </div>
    <div className='perUserStats__cell perUserStats__cell--role'>
      <span className={`perUserStats__roleChip ${roleModifier(row.role)}`}>
        {roleLabel(row.role)}
      </span>
    </div>
    <div className='perUserStats__cell perUserStats__cell--num'>
      {row.proposed_count ?? 0}
    </div>
    <div className='perUserStats__cell perUserStats__cell--num'>
      {row.approved_count ?? 0}
    </div>
    <div className='perUserStats__cell perUserStats__cell--num'>
      {row.denied_count ?? 0}
    </div>
    <div className='perUserStats__cell perUserStats__cell--rate'>
      <ApprovalBar rate={row.approval_rate ?? 0} />
    </div>
    <div className='perUserStats__cell perUserStats__cell--active'>
      {formatLastActive(row.last_active_at)}
    </div>
  </div>
);

// ---------- main ----------

const PerUserStatsView: React.FC<PerUserStatsViewProps> = ({
  data,
  window,
  onWindowChange,
  loading,
  error,
  onRetry,
}) => {
  const users = useMemo(
    () => (Array.isArray(data?.users) ? (data as PerUserStatsResponse).users : []),
    [data]
  );

  return (
    <div className='statsView perUserStats'>
      <WindowChips window={window} onWindowChange={onWindowChange} />

      {error && (
        <div className='statsView__error' role='alert'>
          <span>{error}</span>
          {onRetry && (
            <button
              type='button'
              className='statsView__retryBtn'
              onClick={onRetry}
            >
              Retry
            </button>
          )}
        </div>
      )}

      {loading && !data ? (
        <div className='statsView__loading'>Loading per-user stats…</div>
      ) : users.length === 0 ? (
        <div className='statsSection'>
          <div className='statsSection__muted'>
            No employee activity in this window.
          </div>
        </div>
      ) : (
        <div className='perUserStats__table'>
          <div className='perUserStats__head'>
            <div className='perUserStats__cell perUserStats__cell--employee'>
              Employee
            </div>
            <div className='perUserStats__cell perUserStats__cell--role'>
              Role
            </div>
            <div className='perUserStats__cell perUserStats__cell--num'>
              Proposed
            </div>
            <div className='perUserStats__cell perUserStats__cell--num'>
              Approved
            </div>
            <div className='perUserStats__cell perUserStats__cell--num'>
              Denied
            </div>
            <div className='perUserStats__cell perUserStats__cell--rate'>
              Approval rate
            </div>
            <div className='perUserStats__cell perUserStats__cell--active'>
              Last active
            </div>
          </div>
          {users.map((row) => (
            <PerUserRow key={row.employee_id || row.email} row={row} />
          ))}
        </div>
      )}

      {data && (data.window_start || data.window_end) && (
        <div className='statsView__footer'>
          Window: {data.window_start} → {data.window_end}
        </div>
      )}
    </div>
  );
};

export default PerUserStatsView;
